const SIDES = ['rear', 'right', 'front', 'left']

export const BUILDER_VERSION = 1

const MIAMI_STAGE_SYSTEMS = Object.freeze(['int', 'msr', 'lumo'])
const ORLANDO_STAGE_SYSTEMS = Object.freeze(['int', 'msr'])

export const getAvailableStageSystems = branch => String(branch || '').toLowerCase() === 'orlando'
    ? ORLANDO_STAGE_SYSTEMS
    : MIAMI_STAGE_SYSTEMS

export const BUILDER_PRODUCTS = {
    stage: {
        intModule8: '6671ae6d6243853a701e21b3',
        intModule16: '61e728deb3a6d00a20a9fb04',
        intModule24: '61e55f83161b731824bf2baf',
        intSkirt8: '663e738d6640d51061f7b857',
        intSkirt16: '663e78406f28f3072b3d25e6',
        intSkirt24: '6642425c1a14c8104d40951a',
        intSkirt35: '664243bca3f1e9107795f7a8',
        intSkirt42: '664249cd7db15e107fb031fd',
        intRail: '61afc770cac29540b6f50fc9',
        intSteps: '63c9ab20c7aad0097d2553a8',
        msrLeg: '620baf6a291356870b02ba6b',
        msrLong: '620bb053291356870b02c8c9',
        msrShort: '620bb1d0291356870b02d701',
        msrPlatform: '620bab98291356870b02ac3a',
        msrRailShort: '662910b1571fac7a09da7387',
        msrRailLong: '6629105c571fac7a09da7349',
        msrStairs: '620bf16b291356870b03a19c',
        lumo16Frame: '66511c6c9c1b871064e1b35d',
        lumo8Frame: '66510f6550d66210c9648063',
        lumoTop: '6650f4d3b2b45a10729c2c12',
        lumo8Side: '66510cbe379f4b103b2c8543',
        lumo16Side: '665120d877fcd1107442b7d2',
        uplight: '66269650035d65c299579011'
    },
    wall: {
        module19: '69c567fdaacf4135fe34ec9e',
        module29: '639893d275ae6f6949e77d34',
        module48: '5f3150cfe52cbd37797ad269',
        processorHigh: '63cd8b6b46e0158515c21463',
        processorLow: '6390f13f72d3ee241cfed8d1',
        longEthernet: '67ffdd72691595107d42d85b',
        longPowercon: '61b8a981edabcf0cb75a0727',
        heavyBase: '61d712cd62920e7f4455fe89',
        rigBase: '620bb7e3291356870b030034',
        outrigger: '620bb933291356870b030d9a',
        junction: '5f3150cfe52cbd37797ad268',
        sleeve: '620bb634291356870b02f2b0',
        hoist: '63ee19be8897721d6c99f75d',
        hoistRig: '620bc7a2291356870b0343cf',
        clamps: '61d487545cf8a91145fb04f8',
        doubleClamps: '61d7480262920e7f445713c0',
        plate: '6566562196d82c1000163c01',
        topRig: '63f4fa9a377e8d6193254a27',
        trussTube: '6310e9c1ad1f513d10ba4f6e',
        flat3m: '620bbf8d291356870b031b5a',
        flat2m: '620bc02b291356870b0328c7',
        flat150cm: '620bc0df291356870b03364a',
        truss2m: '5f3150cfe52cbd37797ad273',
        truss1m: '618ac03bf2134332259c899f',
        truss442m: '620aa414291356870b029153',
        ballast: '67990f079506e1106a35525e',
        trussCovers: '61e5661a161b731824bf2c4d'
    }
}

const number = value => Number(value)
const truthy = value => value === true || value === 'true'

const sidesFromLegacy = value => {
    const values = String(value || '').split('.')
    return SIDES.reduce((result, side, index) => ({ ...result, [side]: truthy(values[index]) }), {})
}

const compactSpecs = specs => Object.entries(specs.reduce((result, spec) => {
    if (!spec?.product || !Number.isFinite(Number(spec.units)) || Number(spec.units) <= 0) return result
    result[spec.product] = (result[spec.product] || 0) + Number(spec.units)
    return result
}, {})).map(([product, units]) => ({ product, units }))

const skirtProduct = height => {
    if (number(height) === 8) return BUILDER_PRODUCTS.stage.intSkirt8
    if (number(height) === 16) return BUILDER_PRODUCTS.stage.intSkirt16
    if ([24, 27].includes(number(height))) return BUILDER_PRODUCTS.stage.intSkirt24
    if (number(height) === 35) return BUILDER_PRODUCTS.stage.intSkirt35
    return BUILDER_PRODUCTS.stage.intSkirt42
}

const planPieces = length => {
    if (!Number.isFinite(length) || length < 2) return [0, 0]
    let best = null
    for (let n3 = 0; n3 <= Math.ceil(length / 3) + 3; n3 += 1) {
        const remaining = length - (3 * n3)
        const n2 = remaining > 0 ? Math.ceil(remaining / 2) : 0
        const total = (2 * n2) + (3 * n3)
        const candidate = { n2, n3, overage: total - length, difference: Math.abs(n2 - n3), pieces: n2 + n3 }
        if (candidate.overage < 0) continue
        if (!best || candidate.overage < best.overage ||
            (candidate.overage === best.overage && candidate.difference < best.difference) ||
            (candidate.overage === best.overage && candidate.difference === best.difference && candidate.pieces < best.pieces)) best = candidate
    }
    return [best?.n2 || 0, best?.n3 || 0]
}

export const createStageConfiguration = ({ system, length, width, height, additions = {}, branch, projectDetails = {} }) => ({
    kind: 'stage',
    branch: branch || null,
    system,
    dimensions: { length: number(length), width: number(width), height: number(height), unit: 'feet' },
    accessories: {
        skirt: SIDES.reduce((result, side) => ({ ...result, [side]: Boolean(additions?.Skirt?.[side]) }), {}),
        steps: SIDES.reduce((result, side) => ({ ...result, [side]: Boolean(additions?.Steps?.[side]) }), {}),
        railing: SIDES.reduce((result, side) => ({ ...result, [side]: Boolean(additions?.Railing?.[side]) }), {}),
        lights: Boolean(additions?.lights)
    },
    projectDetails
})

export const createVideoWallConfiguration = ({ mounting, width, height, structureHeight, pitch, branch, existingTruss, projectDetails = {} }) => ({
    kind: 'video_wall',
    branch: branch || null,
    mounting,
    ...(mounting === 'existing' ? {existingTruss} : {}),
    dimensions: {
        width: number(width),
        height: number(height),
        structureHeight: mounting === 'rigged' ? number(structureHeight) : null,
        unit: 'meters'
    },
    pitch: String(pitch),
    resolution: {
        width: Math.round(number(width) * 2 * Math.round(500 / number(pitch))),
        height: Math.round(number(height) * 2 * Math.round(500 / number(pitch)))
    },
    projectDetails
})

export const getBuilderComponentSpecs = configuration => {
    if (!configuration) return []
    if (configuration.kind === 'stage') {
        const { system, dimensions, accessories = {} } = configuration
        const length = number(dimensions?.length)
        const width = number(dimensions?.width)
        const height = number(dimensions?.height)
        const products = BUILDER_PRODUCTS.stage
        const specs = []

        if (system === 'int') {
            specs.push({ product: height === 8 ? products.intModule8 : height === 16 ? products.intModule16 : products.intModule24, units: (width / 4) * (length / 4) })
            const railUnits = SIDES.filter(side => accessories?.railing?.[side]).reduce((sum, side) => sum + (['left', 'right'].includes(side) ? length / 4 : width / 4), 0)
            specs.push({ product: products.intRail, units: railUnits })
        } else if (system === 'lumo') {
            const modules = (width / 2) * (length / 2)
            specs.push({ product: height === 8 ? products.lumo8Frame : products.lumo16Frame, units: modules })
            specs.push({ product: height === 8 ? products.lumo8Side : products.lumo16Side, units: width + length })
            specs.push({ product: products.lumoTop, units: modules })
            specs.push({ product: products.uplight, units: accessories?.lights ? Math.round((width / 4) + (length / 4)) : 0 })
        } else if (system === 'msr') {
            specs.push({ product: products.msrPlatform, units: (width / 8) * (length / 4) })
            specs.push({ product: products.msrLong, units: (width / 8) * ((length / 4) + 1) })
            specs.push({ product: products.msrShort, units: ((width / 8) + 1) * (length / 4) })
            specs.push({ product: products.msrLeg, units: ((width / 8) + 1) * ((length / 4) + 1) })
            specs.push({ product: products.msrRailLong, units: SIDES.filter(side => accessories?.railing?.[side] && !['left', 'right'].includes(side)).reduce(sum => sum + (width / 8), 0) })
            specs.push({ product: products.msrRailShort, units: SIDES.filter(side => accessories?.railing?.[side] && ['left', 'right'].includes(side)).reduce(sum => sum + (length / 4), 0) })
        }

        const skirtFeet = SIDES.filter(side => accessories?.skirt?.[side]).reduce((sum, side) => sum + (['left', 'right'].includes(side) ? length : width), 0)
        specs.push({ product: skirtProduct(height), units: Math.ceil(skirtFeet / 12) })
        specs.push({ product: system === 'msr' ? products.msrStairs : products.intSteps, units: SIDES.filter(side => accessories?.steps?.[side]).length })
        return compactSpecs(specs)
    }

    if (configuration.kind === 'video_wall') {
        const products = BUILDER_PRODUCTS.wall
        const width = number(configuration.dimensions?.width)
        const height = number(configuration.dimensions?.height)
        const structureHeight = number(configuration.dimensions?.structureHeight)
        const pitch = String(configuration.pitch)
        const mounting = configuration.mounting
        const specs = [
            { product: pitch === '1.9' ? products.module19 : pitch === '4.8' ? products.module48 : products.module29, units: height * 4 * width },
            { product: pitch === '4.8' ? products.processorLow : products.processorHigh, units: 1 }
        ]
        const columns = Math.ceil(width * 0.5) + 1
        specs.push({ product: products.longPowercon, units: columns })
        specs.push({ product: products.longEthernet, units: columns })

        if (mounting === 'rigged') {
            let coverUnits = Math.ceil(structureHeight * 0.5) * 2
            specs.push({ product: products.topRig, units: width * 2 })
            specs.push({ product: products.truss2m, units: Math.floor(structureHeight * 0.5) * 2 })
            specs.push({ product: products.truss1m, units: Math.ceil(structureHeight % 2) * 2 })
            if (structureHeight <= 4) {
                coverUnits += Math.ceil(width * 0.5)
                specs.push({ product: products.truss2m, units: Math.floor(width * 0.5) })
                specs.push({ product: products.truss1m, units: Math.ceil(width % 2) })
                specs.push({ product: products.junction, units: 2 })
                specs.push({ product: products.heavyBase, units: 2 })
                specs.push({ product: products.outrigger, units: 4 })
            } else {
                specs.push({ product: products.truss442m, units: Math.ceil(width * 0.5) })
                specs.push({ product: products.sleeve, units: 2 })
                specs.push({ product: products.hoist, units: 2 })
                specs.push({ product: products.hoistRig, units: 2 })
                specs.push({ product: products.rigBase, units: 2 })
                specs.push({ product: products.outrigger, units: 8 })
            }
            specs.push({ product: products.trussCovers, units: coverUnits })
        } else {
            const beamCount = Math.ceil(height / 1.5)
            specs.push({ product: products.truss2m, units: columns * Math.floor(height * 0.5) })
            specs.push({ product: products.truss1m, units: columns * Math.floor(height % 2) })
            specs.push({ product: products.heavyBase, units: columns })
            specs.push({ product: products.ballast, units: columns })
            specs.push({ product: products.clamps, units: columns * 4 * beamCount })
            specs.push({ product: products.plate, units: columns * 4 })
            specs.push({ product: products.doubleClamps, units: columns * 2 * beamCount })
            let widthLeft = width
            const halfPiece = width % 1 ? 1 : 0
            if (halfPiece) widthLeft -= 1.5
            const [n2, n3] = planPieces(widthLeft)
            specs.push({ product: products.flat150cm, units: halfPiece * beamCount })
            specs.push({ product: products.flat2m, units: n2 * beamCount })
            specs.push({ product: products.flat3m, units: n3 * beamCount })
        }
        return compactSpecs(specs)
    }
    return []
}

export const hydrateBuilderComponents = (configuration, products = []) => getBuilderComponentSpecs(configuration).map(spec => {
    const product = products.find(item => String(item?._id) === String(spec.product))
    return product ? { product, units: spec.units, category: product.categories?.[0]?.split('/')?.[1] } : null
}).filter(Boolean)

export const builderEquipmentPrice = components => components.reduce((total, component) => total + ((component.product?.price?.rental || 0) * component.units), 0)

export const builderFromConfiguration = ({ configuration, index, components }) => {
    const builderIndex = number(index)
    let type
    let vars
    if (configuration.kind === 'stage') {
        const { length, width, height } = configuration.dimensions
        const { skirt, steps, railing, lights } = configuration.accessories
        type = `stage//${configuration.system}//${builderIndex}`
        vars = `${length}//${width}//${height}//${SIDES.map(side => Boolean(skirt?.[side])).join('.')}//${SIDES.map(side => Boolean(steps?.[side])).join('.')}//${SIDES.map(side => Boolean(railing?.[side])).join('.')}//${Boolean(lights)}`
    } else if (configuration.kind === 'pipe_drape') {
        type = `drape//chain//${builderIndex}`
        vars = ''
    } else {
        const { structureHeight, width, height } = configuration.dimensions
        type = `wall//${configuration.mounting}//${builderIndex}`
        vars = `${structureHeight || Math.floor(height + 1)}//${width}//${height}//////////`
    }
    const componentSignature = (components || getBuilderComponentSpecs(configuration)).map(component => `${component.product?._id || component.product}:${component.units}`).sort().join('|')
    return {
        type,
        vars,
        version: BUILDER_VERSION,
        configuration,
        component_checksum: componentSignature,
        pricing: {
            equipment_per_day: components?.[0]?.product ? builderEquipmentPrice(components) : null,
            generated_at: new Date().toISOString()
        }
    }
}

export const configurationFromBuilder = builder => {
    if (builder?.version >= BUILDER_VERSION && builder?.configuration?.kind) return builder.configuration
    const [kind, variant] = String(builder?.type || '').split('//')
    const [first, width, height, skirt, steps, railing, lights] = String(builder?.vars || '').split('//')
    if (kind === 'stage') return createStageConfiguration({
        system: variant,
        length: first,
        width,
        height,
        additions: { Skirt: sidesFromLegacy(skirt), Steps: sidesFromLegacy(steps), Railing: sidesFromLegacy(railing), lights: truthy(lights) }
    })
    if (kind === 'wall') return createVideoWallConfiguration({
        mounting: variant,
        structureHeight: first,
        width,
        height,
        pitch: builder?.configuration?.pitch || '2.9'
    })
    return null
}

export const getBuilderIndex = builder => number(String(builder?.type || '').split('//')[2])

export const additionsFromConfiguration = configuration => ({
    Skirt: { ...(configuration?.accessories?.skirt || {}) },
    Steps: { ...(configuration?.accessories?.steps || {}) },
    Railing: { ...(configuration?.accessories?.railing || {}) },
    lights: Boolean(configuration?.accessories?.lights)
})

export const builderSummary = configuration => {
    if (!configuration) return null
    if (configuration.kind === 'pipe_drape') return { title: 'Pipe & Drape', dimensions: `${configuration.drape.height}′ high · ${configuration.drape.sections.reduce((n,s)=>n+s.width,0)}′ long`, details: [`${configuration.drape.sections.length} linked sections`] }
    if (configuration.kind === 'video_wall') {
        const { width, height, structureHeight } = configuration.dimensions
        return {
            title: configuration.mounting === 'existing' ? 'Video wall · Existing truss' : 'Custom video wall',
            dimensions: `${width}m W × ${height}m H`,
            details: [
                configuration.mounting === 'existing' ? 'Mounted on existing truss · no truss added' : configuration.mounting === 'rigged' ? `Truss mounted · ${structureHeight}m structure` : 'Floor mounted',
                `${configuration.pitch}mm pixel pitch`,
                configuration.resolution ? `${configuration.resolution.width} × ${configuration.resolution.height} resolution` : null
            ].filter(Boolean)
        }
    }
    const names = { int: 'Light stage', msr: 'Heavy-duty stage', lumo: 'LUMO stage' }
    const { length, width, height } = configuration.dimensions
    const accessories = configuration.accessories || {}
    const selected = [
        SIDES.some(side => accessories.skirt?.[side]) ? 'Skirt' : null,
        SIDES.filter(side => accessories.steps?.[side]).length ? `${SIDES.filter(side => accessories.steps?.[side]).length} step${SIDES.filter(side => accessories.steps?.[side]).length === 1 ? '' : 's'}` : null,
        SIDES.some(side => accessories.railing?.[side]) ? 'Safety railing' : null,
        accessories.lights ? 'Uplights' : null
    ].filter(Boolean)
    return {
        title: names[configuration.system] || 'Custom stage',
        dimensions: `${length}′ L × ${width}′ W × ${height === 8 && configuration.system === 'int' ? 9 : height}″ H`,
        details: selected.length ? selected : ['No accessories selected']
    }
}
